import type { MarketVariants } from '@/lib/i18n'

export type MethodId = 'card' | 'bank-transfer' | 'payment-app' | 'wire' | 'gift-card' | 'crypto'

export type AskSection = { heading: string; items: string[] }

export type MethodCopy = {
  /** Stable id. The icon for each method is picked by id in the slide. */
  id: MethodId
  label: string
  intro: string
  whatToSay: string
  /**
   * Either a flat list (rendered under the shared `askThemToHeading`) or an
   * array of sections each with its own heading, for when actions are
   * addressed to different parties ("Ask your bank to" + "Ask the app to").
   */
  askThemTo: string[] | AskSection[]
  keepNearby: string[]
  note: string
}

export type Slide4CallBankCopy = {
  eyebrow: string
  title: string
  body: string
  prompt: string
  whatToSayHeading: string
  askThemToHeading: string
  keepNearbyHeading: string
  /** aria-label of the slide-over close button. */
  closeLabel: string
  methods: MethodCopy[]
}

const usMethods: MethodCopy[] = [
  {
    id: 'card',
    label: 'Credit or debit card',
    intro:
      'Call the bank or card company that issued the card. Use the number on the back of the card, your banking app, or the company’s real website. Do not use a number or link the scammer gave you.',
    whatToSay:
      '“I think I’ve been scammed. I made a card payment and need help. Can this charge be stopped, reversed, refunded, or disputed?”',
    askThemTo: [
      'Block any further charges.',
      'Cancel and replace the card if your card details may be exposed.',
      'Check for other suspicious payments.',
      'Give you a reference number for the report.',
    ],
    keepNearby: [
      'The date of the payment.',
      'The amount.',
      'The merchant or company name shown on your statement.',
      'Any emails, texts, receipts, or screenshots.',
    ],
    note:
      'Even if you approved the payment yourself, still call. Your bank or card company can tell you what recovery options are available.',
  },
  {
    id: 'bank-transfer',
    label: 'Bank transfer',
    intro:
      'Call your bank’s fraud line as soon as possible. Use the number on the back of your card, your banking app, or the bank’s official website. Do not use a number the scammer or anyone else gave you.',
    whatToSay:
      '“I think I’ve been scammed. I made a transfer to someone I now believe is a scammer. Can the payment be stopped, recalled, reversed, or refunded?”',
    askThemTo: [
      'Try to recall the transfer or freeze the receiving account.',
      'Check your account for any other suspicious activity.',
      'Block any further payments to that recipient.',
      'Give you a reference number for the report.',
    ],
    keepNearby: [
      'The recipient’s name and account details as you entered them.',
      'The amount and date of the transfer.',
      'The reason you were given for sending the money.',
      'Any messages, calls, or emails from the scammer.',
    ],
    note:
      'Real bank staff will never ask you to move money to a “safe account.” If anyone claims they’re from your bank’s fraud team and tells you to do that, that is the scam.',
  },
  {
    id: 'payment-app',
    label: 'Payment app',
    intro:
      'Call the bank or card linked to the app first. That’s where recovery is most likely. Then open the app and report the transaction as a scam.',
    whatToSay:
      '“I think I’ve been scammed through a payment app. Can this payment be stopped, reversed, refunded, or disputed?”',
    askThemTo: [
      {
        heading: 'Ask your bank or card company to',
        items: [
          'Block further charges from the app.',
          'Check whether your linked account or card is safe.',
          'Give you a reference number for the report.',
        ],
      },
      {
        heading: 'Ask the payment app to',
        items: [
          'Block the person you paid.',
          'Help you secure your app account (change password, turn on two-factor).',
        ],
      },
    ],
    keepNearby: [
      'The name, username, phone number, or email of the person you paid.',
      'The date and amount of the payment.',
      'The transaction ID or receipt.',
      'Screenshots of the chat, payment request, profile, or listing.',
    ],
    note:
      'Don’t send another payment, no matter how convincing they sound. “Unlock,” “refund,” and “verify” are common scripts scammers use to take more.',
  },
  {
    id: 'wire',
    label: 'Wire transfer',
    intro:
      'Call the wire transfer company straight away (Western Union, MoneyGram, Wise, etc.). Use the customer service number printed on your receipt or on their official website. Speed matters. If the money has not been collected yet, they may be able to stop it.',
    whatToSay:
      '“I think I’ve been scammed. I sent a wire transfer to someone I now believe is a scammer. Can the transfer be stopped, halted, or refunded?”',
    askThemTo: [
      'Stop the transfer if the money has not been collected.',
      'Refund the transfer if you are eligible under their fraud policy.',
      'Add the recipient details to their fraud watch list.',
      'Give you a reference number for the report.',
    ],
    keepNearby: [
      'The reference, tracking, or MTCN number from your receipt.',
      'The amount and date of the transfer.',
      'The recipient’s name and the destination country or city.',
      'Any messages, calls, or emails from the scammer.',
    ],
    note:
      'No real company asks you to wire money before they can pay you. If someone says you have to send money to receive money, that is the scam.',
  },
  {
    id: 'gift-card',
    label: 'Gift card',
    intro:
      'Call the gift card company that issued the card (Apple, Amazon, Google Play, eBay, etc.). Use the customer service number printed on the back of the card or on their official website. Speed matters. If the balance hasn’t been spent yet, they can sometimes freeze it.',
    whatToSay:
      '“I think I’ve been scammed. I shared the codes from a gift card with someone I now believe is a scammer. Can you check whether the balance has been used?”',
    askThemTo: [
      'Check the current balance and whether the codes have been redeemed.',
      'Freeze or block the card if the balance is still there.',
      'Flag the codes in their fraud system for investigation.',
      'Give you a reference number for the report.',
    ],
    keepNearby: [
      'The gift card itself. Don’t throw it away.',
      'The receipt from where you bought it.',
      'A photo of both sides of the card, including the redemption code.',
      'Any messages, call records, or screenshots from the scammer.',
    ],
    note:
      'No real business, government agency, or company will ever ask to be paid in gift cards. If someone tells you it’s the only way to settle a bill, fine, or refund, it is a scam.',
  },
  {
    id: 'crypto',
    label: 'Cryptocurrency',
    intro:
      'Open the exchange or wallet app you sent from and report the transaction. If you used a crypto ATM, call the operator using the number on the machine receipt. Speed matters because scammers move funds quickly between wallets to hide the trail.',
    whatToSay:
      '“I think I’ve been scammed. I sent cryptocurrency to a wallet I now believe belongs to a scammer. Can the transaction be reversed, refunded, or the receiving wallet flagged?”',
    askThemTo: [
      'Flag the receiving wallet address in their fraud system.',
      'Freeze your account if there is any suspicious activity.',
      'Help you secure your account (change password, turn on two-factor).',
      'Give you a transaction hash and reference number for your report.',
    ],
    keepNearby: [
      'The transaction hash or ID from the blockchain.',
      'The receiving wallet address you sent to.',
      'The amount, type of crypto (Bitcoin, Ethereum, USDT, etc.), and the date.',
      'Any messages, screenshots, or chat logs from the scammer, including where they first contacted you.',
    ],
    note:
      'No real business, government agency, or company will ever ask to be paid in cryptocurrency. If anyone tells you to pay a tax, fine, refund, or “fee to release funds” in Bitcoin or another crypto, it is the scam.',
  },
]

// Gulf-English overrides, field by field, so the US text stays the single
// source for everything that is market-neutral:
//   - wire: remittances in Qatar go through exchange houses as often as
//     Western Union / MoneyGram counters. Wise is not a household name there.
//   - gift-card: eBay is not a common gift card in Qatar.
//   - crypto: there are no crypto ATMs in Qatar, so that sentence goes.
const qaMethods: MethodCopy[] = usMethods.map(m => {
  if (m.id === 'wire') {
    return {
      ...m,
      intro:
        'Call the exchange house or transfer company straight away (Western Union, MoneyGram, or the exchange house you used). Use the customer service number printed on your receipt or on their official website. Speed matters. If the money has not been collected yet, they may be able to stop it.',
    }
  }
  if (m.id === 'gift-card') {
    return {
      ...m,
      intro:
        'Call the gift card company that issued the card (Apple, Amazon, Google Play, etc.). Use the customer service number printed on the back of the card or on their official website. Speed matters. If the balance hasn’t been spent yet, they can sometimes freeze it.',
    }
  }
  if (m.id === 'crypto') {
    return {
      ...m,
      intro:
        'Open the exchange or wallet app you sent from and report the transaction. Speed matters because scammers move funds quickly between wallets to hide the trail.',
    }
  }
  return m
})

export const copy: MarketVariants<Slide4CallBankCopy> = {
  US: {
    eyebrow: 'Step two',
    title: 'If money moved.',
    body:
      'Call your bank, card company, payment app, or transfer service as soon as possible. Tell them you were scammed and ask if the payment can be stopped, reversed, refunded, or disputed.',
    prompt: 'How was the payment made?',
    whatToSayHeading: 'What to say',
    askThemToHeading: 'Ask them to',
    keepNearbyHeading: 'Keep nearby',
    closeLabel: 'Close',
    methods: usMethods,
  },
  QA: {
    methods: qaMethods,
  },
  ar: {
    eyebrow: 'الخطوة الثانية',
    title: 'إذا دفعت أموالاً.',
    body:
      'اتصل بالبنك، أو شركة البطاقة، أو تطبيق الدفع، أو خدمة التحويل في أسرع وقت ممكن. أخبرهم بأنك تعرّضت للاحتيال، واسألهم إن كان بالإمكان إيقاف الدفعة أو عكسها أو استردادها أو الاعتراض عليها.',
    prompt: 'كيف تم الدفع؟',
    whatToSayHeading: 'ماذا تقول',
    askThemToHeading: 'اطلب منهم',
    keepNearbyHeading: 'جهّز ما يلي',
    closeLabel: 'إغلاق',
    methods: [
      {
        id: 'card',
        label: 'بطاقة ائتمان أو بطاقة خصم',
        intro:
          'اتصل بالبنك أو الشركة التي أصدرت البطاقة. استخدم الرقم المدوّن على ظهر البطاقة، أو تطبيق البنك، أو الموقع الرسمي للشركة. لا تستخدم رقماً أو رابطاً أعطاك إياه المحتال.',
        whatToSay:
          '"أعتقد أنني تعرّضت للاحتيال. أجريت عملية دفع بالبطاقة وأحتاج إلى مساعدة. هل يمكن إيقاف هذه العملية أو عكسها أو استردادها أو الاعتراض عليها؟"',
        askThemTo: [
          'حظر أي عمليات خصم أخرى.',
          'إلغاء البطاقة واستبدالها إذا كانت بياناتها قد تكون مكشوفة.',
          'التحقق من وجود عمليات دفع مشبوهة أخرى.',
          'إعطاءك رقماً مرجعياً للبلاغ.',
        ],
        keepNearby: [
          'تاريخ عملية الدفع.',
          'المبلغ.',
          'اسم التاجر أو الشركة كما يظهر في كشف حسابك.',
          'أي رسائل بريد إلكتروني أو رسائل نصية أو إيصالات أو لقطات شاشة.',
        ],
        note:
          'حتى لو كنت أنت من وافق على عملية الدفع، اتصل على أي حال. يستطيع البنك أو شركة البطاقة إخبارك بخيارات الاسترداد المتاحة.',
      },
      {
        id: 'bank-transfer',
        label: 'تحويل بنكي',
        intro:
          'اتصل بخط الاحتيال في بنكك في أسرع وقت ممكن. استخدم الرقم المدوّن على ظهر بطاقتك، أو تطبيق البنك، أو الموقع الرسمي للبنك. لا تستخدم رقماً أعطاك إياه المحتال أو أي شخص آخر.',
        whatToSay:
          '"أعتقد أنني تعرّضت للاحتيال. أجريت تحويلاً إلى شخص أعتقد الآن أنه محتال. هل يمكن إيقاف التحويل أو استرجاعه أو عكسه أو استرداده؟"',
        askThemTo: [
          'محاولة استرجاع التحويل أو تجميد الحساب المستلم.',
          'التحقق من حسابك بحثاً عن أي نشاط مشبوه آخر.',
          'حظر أي تحويلات أخرى إلى ذلك المستلم.',
          'إعطاءك رقماً مرجعياً للبلاغ.',
        ],
        keepNearby: [
          'اسم المستلم وبيانات حسابه كما أدخلتها.',
          'مبلغ التحويل وتاريخه.',
          'السبب الذي قيل لك من أجله لإرسال المال.',
          'أي رسائل أو مكالمات أو رسائل بريد إلكتروني من المحتال.',
        ],
        note:
          'لن يطلب منك موظفو البنك الحقيقيون أبداً نقل أموالك إلى "حساب آمن". إذا ادّعى أحدهم أنه من فريق مكافحة الاحتيال في بنكك وطلب منك ذلك، فهذا هو الاحتيال بعينه.',
      },
      {
        id: 'payment-app',
        label: 'تطبيق دفع',
        intro:
          'اتصل أولاً بالبنك أو شركة البطاقة المرتبطة بالتطبيق، فهناك تكون فرصة الاسترداد أكبر. ثم افتح التطبيق وأبلغ عن العملية باعتبارها احتيالاً.',
        whatToSay:
          '"أعتقد أنني تعرّضت للاحتيال عبر تطبيق دفع. هل يمكن إيقاف هذه الدفعة أو عكسها أو استردادها أو الاعتراض عليها؟"',
        askThemTo: [
          {
            heading: 'اطلب من البنك أو شركة البطاقة',
            items: [
              'حظر أي عمليات خصم أخرى من التطبيق.',
              'التحقق من أن حسابك أو بطاقتك المرتبطة بالتطبيق آمنة.',
              'إعطاءك رقماً مرجعياً للبلاغ.',
            ],
          },
          {
            heading: 'اطلب من تطبيق الدفع',
            items: [
              'حظر الشخص الذي دفعت له.',
              'مساعدتك في تأمين حسابك في التطبيق (تغيير كلمة المرور، وتفعيل التحقق بخطوتين).',
            ],
          },
        ],
        keepNearby: [
          'اسم الشخص الذي دفعت له، أو اسم المستخدم، أو رقم الهاتف، أو البريد الإلكتروني.',
          'تاريخ الدفعة ومبلغها.',
          'رقم العملية أو الإيصال.',
          'لقطات شاشة للمحادثة أو طلب الدفع أو الملف الشخصي أو الإعلان.',
        ],
        note:
          'لا ترسل أي دفعة أخرى مهما بدا كلامهم مقنعاً. عبارات مثل "فتح الحساب" و"استرداد المبلغ" و"التحقق" هي نصوص شائعة يستخدمها المحتالون لأخذ المزيد.',
      },
      {
        id: 'wire',
        label: 'حوالة مصرفية',
        intro:
          'اتصل فوراً بشركة الصرافة أو شركة التحويل (ويسترن يونيون، أو موني جرام، أو شركة الصرافة التي تعاملت معها). استخدم رقم خدمة العملاء المطبوع على إيصالك أو الموجود على موقعهم الرسمي. السرعة مهمة: إذا لم يُستلم المبلغ بعد، فقد يتمكنون من إيقافه.',
        whatToSay:
          '"أعتقد أنني تعرّضت للاحتيال. أرسلت حوالة إلى شخص أعتقد الآن أنه محتال. هل يمكن إيقاف الحوالة أو تعليقها أو استرداد مبلغها؟"',
        askThemTo: [
          'إيقاف الحوالة إذا لم يُستلم المبلغ بعد.',
          'استرداد مبلغ الحوالة إذا كنت مستحقاً لذلك بموجب سياسة مكافحة الاحتيال لديهم.',
          'إضافة بيانات المستلم إلى قائمة مراقبة الاحتيال لديهم.',
          'إعطاءك رقماً مرجعياً للبلاغ.',
        ],
        keepNearby: [
          'الرقم المرجعي أو رقم التتبع أو رقم MTCN من إيصالك.',
          'مبلغ الحوالة وتاريخها.',
          'اسم المستلم والدولة أو المدينة المرسَل إليها.',
          'أي رسائل أو مكالمات أو رسائل بريد إلكتروني من المحتال.',
        ],
        note:
          'لا توجد شركة حقيقية تطلب منك إرسال حوالة قبل أن تدفع لك. إذا قال لك أحدهم إن عليك إرسال أموال لكي تستلم أموالاً، فهذا هو الاحتيال.',
      },
      {
        id: 'gift-card',
        label: 'بطاقة هدايا',
        intro:
          'اتصل بالشركة التي أصدرت بطاقة الهدايا (آبل، أو أمازون، أو Google Play، وغيرها). استخدم رقم خدمة العملاء المطبوع على ظهر البطاقة أو الموجود على موقعهم الرسمي. السرعة مهمة: إذا لم يُصرف الرصيد بعد، فقد يتمكنون أحياناً من تجميده.',
        whatToSay:
          '"أعتقد أنني تعرّضت للاحتيال. شاركت رموز بطاقة هدايا مع شخص أعتقد الآن أنه محتال. هل يمكنكم التحقق مما إذا كان الرصيد قد استُخدم؟"',
        askThemTo: [
          'التحقق من الرصيد الحالي وما إذا كانت الرموز قد استُخدمت.',
          'تجميد البطاقة أو حظرها إذا كان الرصيد لا يزال موجوداً.',
          'تعليم الرموز في نظام مكافحة الاحتيال لديهم للتحقيق.',
          'إعطاءك رقماً مرجعياً للبلاغ.',
        ],
        keepNearby: [
          'بطاقة الهدايا نفسها. لا تتخلص منها.',
          'إيصال الشراء من المكان الذي اشتريتها منه.',
          'صورة لوجهي البطاقة، بما في ذلك رمز الاستخدام.',
          'أي رسائل أو سجلات مكالمات أو لقطات شاشة من المحتال.',
        ],
        note:
          'لن تطلب منك أي شركة أو جهة حكومية أو مؤسسة حقيقية الدفع ببطاقات الهدايا أبداً. إذا قال لك أحدهم إنها الطريقة الوحيدة لتسوية فاتورة أو غرامة أو استرداد مبلغ، فهذا احتيال.',
      },
      {
        id: 'crypto',
        label: 'العملات الرقمية',
        intro:
          'افتح منصة التداول أو تطبيق المحفظة الذي أرسلت منه وأبلغ عن العملية. السرعة مهمة، لأن المحتالين ينقلون الأموال بسرعة بين المحافظ لإخفاء أثرها.',
        whatToSay:
          '"أعتقد أنني تعرّضت للاحتيال. أرسلت عملات رقمية إلى محفظة أعتقد الآن أنها تعود لمحتال. هل يمكن عكس العملية أو استرداد المبلغ أو وضع إشارة على المحفظة المستلمة؟"',
        askThemTo: [
          'وضع إشارة على عنوان المحفظة المستلمة في نظام مكافحة الاحتيال لديهم.',
          'تجميد حسابك إذا كان هناك أي نشاط مشبوه.',
          'مساعدتك في تأمين حسابك (تغيير كلمة المرور، وتفعيل التحقق بخطوتين).',
          'إعطاءك رمز العملية (hash) ورقماً مرجعياً لبلاغك.',
        ],
        keepNearby: [
          'رمز العملية (hash) أو رقمها من سلسلة الكتل.',
          'عنوان المحفظة التي أرسلت إليها.',
          'المبلغ، ونوع العملة الرقمية (بيتكوين، إيثريوم، USDT، وغيرها)، والتاريخ.',
          'أي رسائل أو لقطات شاشة أو سجلات محادثة من المحتال، بما في ذلك المكان الذي تواصل معك فيه أول مرة.',
        ],
        note:
          'لن تطلب منك أي شركة أو جهة حكومية أو مؤسسة حقيقية الدفع بالعملات الرقمية أبداً. إذا طلب منك أحدهم دفع ضريبة أو غرامة أو استرداد أو "رسوم للإفراج عن الأموال" بالبيتكوين أو أي عملة رقمية أخرى، فهذا هو الاحتيال.',
      },
    ],
  },
}
